import { useRef, type ReactNode } from "react";
import { useFaqAccordion } from "../hooks/useFaqAccordion";

type Item = {
  num: string;
  question: string;
  answer: ReactNode;
};

const ITEMS: Item[] = [
  {
    num: "01.",
    question: "What's included for $5,125/month?",
    answer:
      "Unlimited product design requests — UI, UX, AI-native prototypes, design systems, landing pages, pitch decks, brand work. One or two tasks active at a time, the rest sit in your queue.",
  },
  {
    num: "02.",
    question: "Why \"real code\" and not just Figma?",
    answer:
      "Figma files don't ship. AI-native prototypes do. By designing in code from day one, we cut the handoff step — your engineers get something to fork, your users get something to click, and you get to test ideas in days instead of sprints.",
  },
  {
    num: "03.",
    question: "How fast is turnaround?",
    answer:
      "Fast. We work in your tools and ship as we go, so you see progress every few days instead of waiting weeks for a big handoff. Bigger work like full feature designs or pitch decks takes longer, but it's never a black box.",
  },
  {
    num: "04.",
    question: "Can I pause or cancel?",
    answer:
      "Yes. Pause for a month if your roadmap slows down, cancel any time with no penalty. You only pay for the months you actually use.",
  },
  {
    num: "05.",
    question: "Why subscription instead of a full-time hire?",
    answer:
      "A senior product designer in the US runs $195K–$240K all-in with equity and benefits. Most seed and Series A teams don't have 40 hours of design work every week, but they still need senior-level output. Subscription means you get the quality without the headcount.",
  },
  {
    num: "06.",
    question: "Why subscription instead of a freelancer or an agency?",
    answer:
      "Freelancers go quiet for weeks. Agencies are slow, expensive, and pass you off to a junior. N801 sits in between — one fixed price, one senior designer who learns your product and stays embedded.",
  },
  {
    num: "07.",
    question: "What if I'm not happy with the work?",
    answer: "Unlimited revisions. We iterate until it's right.",
  },
  {
    num: "08.",
    question: "Do you only work on software?",
    answer:
      "Mostly. The sweet spot is product design for AI-native software companies — SaaS, mobile apps, AI tools, dev tools. Marketing and pitch work happens too, usually for the same clients.",
  },
  {
    num: "09.",
    question: "Who's behind N801?",
    answer: (
      <>
        N801 is run by{" "}
        <a
          href="https://www.linkedin.com/in/nathan-alspaugh/"
          target="_blank"
          rel="noopener noreferrer"
        >
          Nathan Alspaugh
        </a>
        , a senior product designer based out of the Davis County area in Utah.
        He has experience designing for startups in both the bay area and in
        silicon slopes.
      </>
    ),
  },
];

function FaqItem({ item }: { item: Item }) {
  const ref = useRef<HTMLDetailsElement | null>(null);
  useFaqAccordion(ref);

  return (
    <details className="faq-item" data-reveal ref={ref}>
      <summary>
        <span className="faq-num">{item.num}</span>
        <span className="faq-q">{item.question}</span>
        <span className="faq-icon" aria-hidden="true">+</span>
      </summary>
      <div className="faq-body">
        <div className="faq-body-inner">
          <p>{item.answer}</p>
        </div>
      </div>
    </details>
  );
}

export function Faq() {
  return (
    <section className="faq-section" aria-labelledby="s4-title">
      <div className="faq-grid">
        <div className="faq-grid-col">
          <div className="faq-head" data-reveal>
            <h2 id="s4-title" className="faq-heading">Frequently asked questions</h2>
          </div>
          <div className="faq-list">
            {ITEMS.map((item) => (
              <FaqItem key={item.num} item={item} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
